import I9DocumentTitle from '@components/i9-document-title';
import { documentTypeBackSuffix } from '@dtos/document-type';
import { RepresentativeCandidateDocumentDetails } from '@dtos/remote-i9-details';
import { createTranslationService } from '@services/factories';
import { IRemoteRepresentativeStore } from '@stores/remote-representative/remote-representative-store';
import { cacheTranslations } from '@utils/cache-translations';
import { formatAsUTC } from '@utils/date-format';
import {
    filter,
    flatten,
    keys,
    reduce,
    sortBy
} from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';
import {
    DetailsCell,
    DetailsColumnContainer,
    DetailsContainer
} from './details-styles';

interface Props {
    store: IRemoteRepresentativeStore;
}

enum DocumentDetailsTranslation {
    middleInitial = 'middleInitial',
    documentTitle = 'documentTitle',
    issuingAuthority = 'issuingAuthority',
    documentNumber = 'documentNumber',
    expirationDate = 'expirationDate',
    naLabel = 'naLabel'
}

export const isBackSideOfDocument = (documentType: string): boolean => documentType.includes(documentTypeBackSuffix);

export const sortDocuments = (
    documentDetails: RepresentativeCandidateDocumentDetails[]
): RepresentativeCandidateDocumentDetails[] => {
    const documentPairs = reduce<RepresentativeCandidateDocumentDetails,
        Record<string, RepresentativeCandidateDocumentDetails[]>>(
        documentDetails,
        (groups, document) => {
            const keyWithoutBack = document.documentType.replace(documentTypeBackSuffix, '');
            if (!groups[keyWithoutBack]) {
                groups[keyWithoutBack] = [];
            }

            groups[keyWithoutBack].push(document);
            // assumes that the back sides of documents will always have a longer string length
            groups[keyWithoutBack] = sortBy(groups[keyWithoutBack], ({ documentType }) => documentType.length);

            return groups;
        },
        {}
    );

    return flatten(
        keys(documentPairs).sort().map(key => documentPairs[key])
    );
};

const DocumentDetails = ({ store }: Props) => {
    const translations = cacheTranslations(
        createTranslationService(),
        DocumentDetailsTranslation
    );

    const renderDocumentDetail = (document: RepresentativeCandidateDocumentDetails) => {
        const fallBackDocumentTypeName = document?.documentType ?? translations.naLabel;
        const documentDetail = store.documentTypesValidationsAll.find(
          (item) => item.documentType === document?.documentType
        );
        return (
            <DetailsContainer key={document.documentId} useSeparator={true}>
                <DetailsColumnContainer flex={160}>
                    <DetailsCell>{translations.documentTitle}</DetailsCell>
                    <DetailsCell>{translations.issuingAuthority}</DetailsCell>
                    <DetailsCell>{translations.documentNumber}</DetailsCell>
                    <DetailsCell>{translations.expirationDate}</DetailsCell>
                </DetailsColumnContainer>
                <DetailsColumnContainer>
                    <DetailsCell>
                        <I9DocumentTitle documentType={document?.documentType}
                                         fallbackName={fallBackDocumentTypeName} documentTitle={documentDetail?.title ?? ''}/>
                    </DetailsCell>
                    <DetailsCell>{document?.issuingAuthority}</DetailsCell>
                    <DetailsCell>{document?.documentNumber}</DetailsCell>
                    <DetailsCell>
                        {
                            document?.expiryDate ?
                            formatAsUTC(document.expiryDate) :
                            translations.naLabel
                        }
                    </DetailsCell>
                </DetailsColumnContainer>
            </DetailsContainer>
        );
    };

    const documentDetails = store.remoteI9Details?.documentDetails;
    if (!documentDetails?.length) {
        return null;
    }

    const filteredDocuments = filter(documentDetails, ({ documentType }) => !isBackSideOfDocument(documentType));
    const sortedDocuments = sortDocuments(filteredDocuments);

    return (
        <>{sortedDocuments?.map(renderDocumentDetail)}</>
    );
};

export default observer(DocumentDetails);



// WEBPACK FOOTER //
// ./src/screens/review-section3/review/document-details.tsx